import { generateSocketURI, getRangerBaseUrl } from "./helpers";

const RECONNECT_MS = 1000;

/**
 * Singleton-friendly Rango WebSocket client.
 * Supports public (and optional private via custom base path).
 */
export default class RangerClient {
  constructor() {
    this.ws = null;
    this.buffer = [];
    this.subscriptions = new Set();
    this.handlers = new Set();
    this.connectionHandlers = new Set();
    this.withAuth = false;
    this.baseUrl = "";
    this.privateBaseUrl = "";
    this.reconnectTimer = null;
    this.intentionalClose = false;
    this.connected = false;
    this.connecting = false;
  }

  onMessage(fn) {
    this.handlers.add(fn);
    return () => this.handlers.delete(fn);
  }

  onConnectionChange(fn) {
    this.connectionHandlers.add(fn);
    return () => this.connectionHandlers.delete(fn);
  }

  _emitConnection() {
    const snap = {
      connected: this.connected,
      connecting: this.connecting,
      withAuth: this.withAuth,
      subscriptions: [...this.subscriptions],
    };
    this.connectionHandlers.forEach((fn) => {
      try {
        fn(snap);
      } catch (e) {
        console.error("[ranger] connection handler", e);
      }
    });
  }

  _emitMessage(routingKey, event) {
    this.handlers.forEach((fn) => {
      try {
        fn(routingKey, event);
      } catch (e) {
        console.error("[ranger] message handler", e);
      }
    });
  }

  connect({ withAuth = false, streams = [], privateBaseUrl } = {}) {
    this.intentionalClose = false;
    this.withAuth = withAuth;
    this.baseUrl = getRangerBaseUrl();
    this.privateBaseUrl = privateBaseUrl || "";

    const publicOrPrivate = withAuth && this.privateBaseUrl
      ? this.privateBaseUrl
      : `${this.baseUrl}/${withAuth ? "private" : "public"}`;

    const initial = Array.from(
      new Set(["global.tickers", ...streams, ...this.subscriptions])
    );
    initial.forEach((s) => this.subscriptions.add(s));

    this._open(publicOrPrivate, initial);
  }

  _open(base, streams) {
    this._clearReconnect();
    if (this.ws) {
      try {
        this.ws.onclose = null;
        this.ws.close();
      } catch (_) {
        /* ignore */
      }
      this.ws = null;
    }

    this.connecting = true;
    this.connected = false;
    this._emitConnection();

    const uri = generateSocketURI(base, streams);
    let ws;
    try {
      ws = new WebSocket(uri);
    } catch (e) {
      console.error("[ranger] open failed", e);
      this.connecting = false;
      this._scheduleReconnect();
      return;
    }

    this.ws = ws;

    ws.onopen = () => {
      this.connected = true;
      this.connecting = false;
      this._emitConnection();
      while (this.buffer.length) {
        const msg = this.buffer.shift();
        ws.send(JSON.stringify(msg));
      }
    };

    ws.onerror = (err) => {
      console.warn("[ranger] socket error", err);
    };

    ws.onclose = () => {
      this.connected = false;
      this.connecting = false;
      this.ws = null;
      this._emitConnection();
      if (!this.intentionalClose) {
        this._scheduleReconnect();
      }
    };

    ws.onmessage = ({ data }) => {
      if (data === "pong" || data === "ping") {
        if (data === "ping" && ws.readyState === WebSocket.OPEN) {
          ws.send("pong");
        }
        return;
      }

      let payload = {};
      try {
        payload = JSON.parse(data);
      } catch (e) {
        console.error("[ranger] parse error", e);
        return;
      }

      Object.keys(payload).forEach((routingKey) => {
        this._emitMessage(routingKey, payload[routingKey]);
      });
    };
  }

  _scheduleReconnect() {
    this._clearReconnect();
    this.reconnectTimer = setTimeout(() => {
      if (this.intentionalClose) return;
      const base =
        this.withAuth && this.privateBaseUrl
          ? this.privateBaseUrl
          : `${this.baseUrl}/${this.withAuth ? "private" : "public"}`;
      this._open(base, [...this.subscriptions]);
    }, RECONNECT_MS);
  }

  _clearReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  send(payload) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    } else {
      this.buffer.push(payload);
    }
  }

  subscribe(streams) {
    const list = (streams || []).filter(Boolean);
    if (!list.length) return;
    list.forEach((s) => this.subscriptions.add(s));
    this.send({ event: "subscribe", streams: list });
  }

  unsubscribe(streams) {
    const list = (streams || []).filter(Boolean);
    if (!list.length) return;
    list.forEach((s) => this.subscriptions.delete(s));
    this.send({ event: "unsubscribe", streams: list });
  }

  /** Force close + reopen (e.g. bad order-book sequence). */
  reconnect() {
    this.intentionalClose = false;
    if (this.ws) {
      try {
        this.ws.close();
      } catch (_) {
        /* ignore */
      }
    } else {
      this._scheduleReconnect();
    }
  }

  disconnect() {
    this.intentionalClose = true;
    this._clearReconnect();
    if (this.ws) {
      try {
        this.ws.close();
      } catch (_) {
        /* ignore */
      }
      this.ws = null;
    }
    this.connected = false;
    this.connecting = false;
    this._emitConnection();
  }
}
